import { Sender } from '@ant-design/x';
import { Grid, Tooltip } from 'antd';
import { BalanceAIColoredIcon } from 'common/icons';
import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { AIGradientColors } from 'src/theme/colors';

import { CropwiseAiTabsEnum } from '../cropwise-ai.enum';
import { useCropwiseAiInput } from './cropwise-ai-input.hook';
import * as Styled from './cropwise-ai-input.style';
import { CropwiseAiInputSuffix } from './cropwise-ai-input-suffix/cropwise-ai-input-suffix.component';

interface CropwiseAiInputProps {
  showType: CropwiseAiTabsEnum;
}

const renderInputSuffix = (
  components: ComponentProps<typeof CropwiseAiInputSuffix>['components'],
  isPending: boolean,
  value: string,
) => (
  <CropwiseAiInputSuffix
    components={components}
    isPending={isPending}
    value={value}
  />
);

const CropwiseAiInput = ({ showType }: CropwiseAiInputProps) => {
  const { t } = useTranslation();
  const isMobile = Grid.useBreakpoint()?.xs;
  const {
    isPending,
    handleSendMessage,
    startRecording,
    stopRecording,
    isRecording,
    value,
    setValue,
  } = useCropwiseAiInput();

  if (showType === CropwiseAiTabsEnum.HIDE) {
    return null;
  }

  return (
    <Styled.Container>
      <Sender
        value={value}
        autoSize={{ minRows: 1, maxRows: 5 }}
        onChange={(v) => {
          setValue(v);
        }}
        allowSpeech={{
          recording: isRecording,
          onRecordingChange: (recording) =>
            recording ? void startRecording() : stopRecording(),
        }}
        suffix={(_, info) =>
          renderInputSuffix(info.components, isPending, value)
        }
        onSubmit={(v) => void handleSendMessage(v)}
        placeholder={t('cropwise_ai.input.placeholder') || ''}
        prefix={
          <Styled.BoxIcon>
            <BalanceAIColoredIcon />
          </Styled.BoxIcon>
        }
      />
      <Tooltip
        title={t('cropwise_ai.input.tooltip')}
        color={AIGradientColors[50]}
        styles={{ root: { maxWidth: isMobile ? '80%' : 500 } }}
      >
        <Styled.Text>
          <Styled.WarningText>
            {t('cropwise_ai.input.warning')}
          </Styled.WarningText>
          <Styled.VerifyText>
            {t('cropwise_ai.input.check_information')}
          </Styled.VerifyText>
        </Styled.Text>
      </Tooltip>
    </Styled.Container>
  );
};

export { CropwiseAiInput };
